import { Box, LinearProgress, Paper, Skeleton, TablePagination, Typography } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid";
import { useTranslate } from "@refinedev/core";

import { CategoryMedal } from "@modules/financial/components/CategoryMedal";
import { ClientsCountCell } from "@modules/financial/components/ClientsCountCell";
import {
  ClientsByCategoryRow,
  FinancialRow,
  PlansByCategoryRow,
} from "@modules/financial/types";

import { ClientsCountColumnActions } from "./columns";

interface CategorySummaryProps {
  tab: "clients_by_category" | "plans_by_category";
  rows: FinancialRow[];
  loading: boolean;
  count: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  clientsCount?: ClientsCountColumnActions;
}

export function CategorySummary({
  tab,
  rows,
  loading,
  count,
  paginationModel,
  onPaginationModelChange,
  clientsCount,
}: CategorySummaryProps) {
  const translate = useTranslate();
  const showPager = count > paginationModel.pageSize;
  const singleRow = tab === "clients_by_category";
  const cardsLayout = singleRow
    ? {
        display: "flex",
        flexWrap: "nowrap",
        gap: 1,
        p: 1.5,
        width: "100%",
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: 2,
        p: 2,
      };

  return (
    <Box>
      {loading && <LinearProgress />}
      {loading && rows.length === 0 ? (
        <Box
          sx={cardsLayout}
        >
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} variant="rounded" height={tab === "clients_by_category" ? 148 : 112} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ px: 2, py: 6, textAlign: "center" }}>
          {translate("financial_admin.messages.empty")}
        </Typography>
      ) : (
        <Box
          sx={{ ...cardsLayout, opacity: loading ? 0.55 : 1 }}
        >
          {tab === "plans_by_category"
            ? rows.map((row) => {
                const item = row as PlansByCategoryRow;
                return <PlansCard key={item.level_id} row={item} />;
              })
            : rows.map((row) => {
                const item = row as ClientsByCategoryRow;
                return (
                  <ClientsCard
                    key={item.level_id}
                    row={item}
                    clientsCount={clientsCount}
                  />
                );
              })}
        </Box>
      )}
      {showPager && (
        <TablePagination
          component="div"
          count={count}
          page={paginationModel.page}
          rowsPerPage={paginationModel.pageSize}
          onPageChange={(_, page) => onPaginationModelChange({ ...paginationModel, page })}
          onRowsPerPageChange={(event) =>
            onPaginationModelChange({ page: 0, pageSize: Number(event.target.value) })
          }
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}
    </Box>
  );
}

function PlansCard({ row }: { row: PlansByCategoryRow }) {
  const translate = useTranslate();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderLeft: 4,
        borderLeftColor: "primary.main",
      }}
    >
      <CategoryMedal name={row.level_name} size={64} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {row.level_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate("financial_admin.fields.level_rank")} {row.level_rank}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="h4" fontWeight={700} color="secondary.main" lineHeight={1}>
          {row.plans_count}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {translate("financial_admin.fields.plans_count")}
        </Typography>
      </Box>
    </Paper>
  );
}

function ClientsCard({
  row,
  clientsCount,
}: {
  row: ClientsByCategoryRow;
  clientsCount?: ClientsCountColumnActions;
}) {
  const translate = useTranslate();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        borderLeft: 4,
        borderLeftColor: "primary.main",
        flex: "1 1 0",
        width: 0,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, minWidth: 0 }}>
        <CategoryMedal name={row.level_name} size={40} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {row.level_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("financial_admin.fields.level_rank")} {row.level_rank}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {translate("financial_admin.fields.clients_count")}
      </Typography>
      {clientsCount ? (
        <ClientsCountCell
          row={row}
          listParams={clientsCount.listParams}
          writeParams={clientsCount.writeParams}
        />
      ) : (
        <Typography variant="h5" fontWeight={700}>
          {row.clients_count}
        </Typography>
      )}
    </Paper>
  );
}
